'use client';

import { useCallback, MouseEvent, ReactNode } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';

import {
  Pagination as ShadcnPagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils/cn';
import { C_PAGINATIONDEFAULT } from '@/ts/constants/C_PAGINATIONDEFAULT';

export interface I_PaginationDefaultProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export function PaginationDefault({
  currentPage,
  totalPages,
  onPageChange,
  className
}: I_PaginationDefaultProps) {
  const t = useTranslations('accessibility');

  const handlePageClick = useCallback(
    (page: number) => (e: MouseEvent) => {
      e.preventDefault();
      onPageChange(page);
    },
    [onPageChange]
  );

  const renderPageLink = useCallback(
    (page: number, isActive: boolean, extraClassName?: string) => (
      <PaginationItem key={page}>
        <PaginationLink
          href="#"
          onClick={handlePageClick(page)}
          isActive={isActive}
          className={cn(
            'text-foreground transition-all duration-200',
            isActive && 'border-primary-border',
            extraClassName
          )}
        >
          {page}
        </PaginationLink>
      </PaginationItem>
    ),
    [handlePageClick]
  );

  const renderPageNumbers = useCallback(() => {
    const pages: ReactNode[] = [];
    const { MAX_VISIBLE_PAGES } = C_PAGINATIONDEFAULT;

    if (totalPages <= MAX_VISIBLE_PAGES) {
      // Show all pages if total is small (no ellipsis for 3 or fewer pages)
      for (let i = 1; i <= totalPages; i++) {
        pages.push(renderPageLink(i, i === currentPage));
      }
    } else {
      // Always show first page
      pages.push(renderPageLink(1, 1 === currentPage));

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      // Show ellipsis if there's a gap between page 1 and the start of middle pages
      if (start > 2) {
        pages.push(
          <PaginationItem key="ellipsis-start">
            <PaginationEllipsis className="text-foreground" />
          </PaginationItem>
        );
      }

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(renderPageLink(i, i === currentPage));
        }
      }

      // Show ellipsis if there's a gap between the last shown page and the last page
      if (end < totalPages - 1) {
        pages.push(
          <PaginationItem key="ellipsis-end">
            <PaginationEllipsis className="text-foreground" />
          </PaginationItem>
        );
      }

      // Always show last page if more than 1 page
      if (totalPages > 1) {
        pages.push(
          renderPageLink(
            totalPages,
            totalPages === currentPage,
            'hover:bg-muted'
          )
        );
      }
    }

    return pages;
  }, [currentPage, totalPages, renderPageLink]);

  if (totalPages <= 1) {
    return null;
  }

  return (
    <ShadcnPagination className={cn('', className)}>
      <PaginationContent>
        {/* Previous Button */}
        <PaginationItem>
          <PaginationLink
            href="#"
            onClick={handlePageClick(currentPage - 1)}
            size="icon"
            className={cn(
              'text-foreground hover:bg-muted',
              currentPage === 1 && 'pointer-events-none opacity-50'
            )}
            aria-label={t('goToPreviousPage')}
          >
            <ChevronLeft className="h-4 w-4" />
          </PaginationLink>
        </PaginationItem>

        {/* Page Numbers with Ellipsis */}
        {renderPageNumbers()}

        {/* Next Button */}
        <PaginationItem>
          <PaginationLink
            href="#"
            onClick={handlePageClick(currentPage + 1)}
            size="icon"
            className={cn(
              'text-foreground hover:bg-muted',
              currentPage === totalPages && 'pointer-events-none opacity-50'
            )}
            aria-label={t('goToNextPage')}
          >
            <ChevronRight className="h-4 w-4" />
          </PaginationLink>
        </PaginationItem>
      </PaginationContent>
    </ShadcnPagination>
  );
}
